import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { BrandedHeader } from "@/components/Common/BrandedHeader/BrandedHeader";
import { Footer } from "@/component-library/Footer";
import { getQueryParams, uuidv4 } from "@/utils/filters";
import { HCP_WORKFLOW_STEPS } from "@/constants/hcpWorkflow";
import { useWorkflowError } from "@/lib/hooks/useWorkflowError";
import { workflowRequests } from "@/api/(requests)/workflow";

import { CallScheduledReferOtherPatient } from "./CallScheduledReferOtherPatient";
import { CallSkippedReferOtherPatient } from "./CallSkippedReferOtherPatient";
import HCPInfo from "./hcpInfo";
import HCPPatientInfo from "./HCPPatientInfo";
import ReferAnotherPatientOrScheduleAppointment from "./referAnotherPatientOrScheduleAppointment";

import "../styles.scss";

const HCPWorkflow = (props) => {
  const [sharedWorkflowData, setSharedWorkflowData] = useState({});
  const [currentStepName, setCurrentStepName] = useState(null);
  const dispatch = useDispatch();
  const showWorkflowErrorAlert = useWorkflowError();

  const update_state_and_get_next_node = async (state, node_name, successToast) => {
    try {
      const data = {
        node_name: node_name,
        state: state,
        workflow_instance_entity_id: sharedWorkflowData.workflow_instance.entity_id,
        workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
      };
      const response = await workflowRequests.update_state_and_get_next_node({ data });

      if (successToast) {
        dispatch(
          addToast({
            message: successToast,
            messageType: toastMessageTypes.success_v2,
          }),
        );
      }

      // if workflow has finished redirect to home page
      if (response.workflow_instance.current_node_name === null) {
        props.history.push("");

        return;
      }

      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });

      window.scrollTo({ behavior: "instant", top: 0 });
    } catch (e) {
      console.log(e.message);
      showWorkflowErrorAlert(e);
    }
  };

  const baseProps = {
    currentStepName,
    sharedWorkflowData,
    update_state_and_get_next_node,
  };

  // the following are the workflow steps that are acted on by the client
  // the other steps are present in dynamo happen in the background
  const workflowSteps = {
    [HCP_WORKFLOW_STEPS.call_scheduled_refer_another_patient]: (
      <CallScheduledReferOtherPatient {...baseProps} />
    ),
    [HCP_WORKFLOW_STEPS.call_skipped_refer_another_patient]: (
      <CallSkippedReferOtherPatient {...baseProps} />
    ),
    [HCP_WORKFLOW_STEPS.hcp_info]: <HCPInfo {...baseProps} />,
    [HCP_WORKFLOW_STEPS.patient_info]: <HCPPatientInfo {...baseProps} />,
    [HCP_WORKFLOW_STEPS.schedule_call_or_skip]: (
      <ReferAnotherPatientOrScheduleAppointment {...baseProps} />
    ),
  };

  useEffect(() => {
    (async () => {
      if (!currentStepName) {
        const data = { workflow_name: "HCP_WORKFLOW" };
        const workflowResponse = await workflowRequests.get_workflow({ data });

        if (workflowResponse) {
          const data = {
            entity_type: "session",
            workflow_config_entity_id: workflowResponse.entity_id,
            workflow_instance_entity_id: uuidv4(),
            workflow_name: workflowResponse.workflow_name,
          };
          const response = await workflowRequests.create_workflow_instance({ data });

          setSharedWorkflowData({ ...sharedWorkflowData, workflow_instance: response });
        }
      } else if (
        !sharedWorkflowData.workflow_instance ||
        (currentStepName && !Object.hasOwn(workflowSteps, currentStepName))
      ) {
        setCurrentStepName(null);
        props.history.push("/app/hcp-workflow");
      }
    })();
  }, [currentStepName]);

  //display workflow step based on changes to workflow instance
  useEffect(() => {
    let stepName =
      sharedWorkflowData.workflow_instance &&
      sharedWorkflowData.workflow_instance.current_node_name;

    if (sharedWorkflowData.workflow_instance && !stepName) {
      stepName = sharedWorkflowData.workflow_instance.first_node;
    }

    if (stepName) {
      // adds param to url so we can display the right header
      // on the new patient referral
      if (
        (currentStepName === HCP_WORKFLOW_STEPS.call_scheduled_refer_another_patient ||
          currentStepName === HCP_WORKFLOW_STEPS.call_skipped_refer_another_patient) &&
        stepName === HCP_WORKFLOW_STEPS.patient_info
      ) {
        props.history.push(`/app/hcp-workflow?step=${stepName}&referring_another_patient=true`);
      } else {
        props.history.push(`/app/hcp-workflow?step=${stepName}`);
      }

      setCurrentStepName(stepName);
    }
  }, [sharedWorkflowData.workflow_instance]);

  // display workflow step based on changes to nav buttons (go back/forward)
  useEffect(() => {
    // If back button is ever hit in workflow, automatically reset workflow to start point
    if (props.history.action === "POP") {
      setCurrentStepName(null);
      setSharedWorkflowData({});
      props.history.push("/app/hcp-workflow");

      return;
    }

    const stepName = getQueryParams("step", props.location.search);

    if (
      !sharedWorkflowData.workflow_instance ||
      (stepName &&
        stepName !== currentStepName &&
        stepName === sharedWorkflowData.workflow_instance.first_node)
    ) {
      setCurrentStepName(null);
      setSharedWorkflowData({});
      props.history.push("/app/hcp-workflow");
    } else if (
      stepName &&
      stepName !== currentStepName &&
      stepName !== sharedWorkflowData.workflow_instance.first_node
    ) {
      setCurrentStepName(stepName);
      props.history.push(`/app/hcp-workflow?step=${stepName}`);
    }
  }, [getQueryParams("step", props.location.search)]);

  return (
    <>
      <div className="self-signup-content" data-public>
        <div>
          <BrandedHeader className="HCPContainer-header" />
          {currentStepName &&
            Object.hasOwn(workflowSteps, currentStepName) &&
            workflowSteps[currentStepName]}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default withRouter(HCPWorkflow);
